import {
  Box,
  Card,
  CardContent,
  Skeleton,
  useMediaQuery,
} from "@mui/material";
import PieChartOutlineOutlined from "@mui/icons-material/PieChartOutlineOutlined";
import AnalyticsWidgetHeader from "./AnalyticsWidgetHeader";
import {
  dashboardWidgetHeaderSx,
  dashboardWidgetFiltersSx,
  DASHBOARD_CHART_WIDGET_HEIGHT,
} from "./dashboardWidgetLayout";
import { useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { LoginResponse } from "subModule/src/services/userProfile/interface";
import { EnqueueSnackbar } from "notistack";
import AlertMessages from "common/Alert/AlertMessages";
import { Controller, useForm } from "react-hook-form";
import DynamicSelect from "common/Select/Select";
import { GetStationExpenseTypeResponse } from "subModule/src/services/analytics/interface";
import { useGetStationExpenseTypeService } from "subModule/src/services/analytics/useAnalytics";
import { TFunction } from "i18next";
import { BarChart } from "@mui/x-charts/BarChart";
import { InfiniteData } from "@tanstack/react-query";
import { GetStationDropdownResponse } from "subModule/src/services/station/interface";
interface ExpensesByTypeProps {
  user: LoginResponse | undefined;
  themes: any;
  stations: { id: number; name: string }[] | [];
  enqueueSnackbar: EnqueueSnackbar;
  stationsLoaded: boolean;
  handleDropdownActions: (
    typeName: string,
    action: "searchIt" | "loadNext",
    search?: string,
  ) => void;
  setPageNo: any;
  stationsData: InfiniteData<GetStationDropdownResponse, unknown> | undefined;
  searchLoading: string | null;
  pageNo: number;
  dropdownSearchTerm: any;
  setDropdownSearchTerm: any;
  globalFilters: { stationId: any; range: any; allStation: boolean };
  globalFilterApplied: boolean;
  setGlobalFilterApplied: (value: boolean) => void;
}
export default function ExpensesByType(props: ExpensesByTypeProps) {
  const { t, i18n } = useTranslation();
  const firstRender = useRef(true);
  const screenWidth = useMediaQuery(
    "(min-width: 1200px) and (max-width: 1400px",
  );
  const [data, setData] = useState<any>(null);
  const [loading, setLoading] = useState<boolean>(true);
  const {
    control,
    formState: { errors },
    setValue,
    watch,
  } = useForm<{ [key: string]: any }>({
    defaultValues: {
      range: { value: "monthly", label: t("mtd") },
      allStation: false,
    },
  });
  const filters = useMemo(
    () => [
      { value: "daily", label: t("today") },
      { value: "yesterday", label: t("yesterday") },
      { value: "weekly", label: t("wtd") },
      { value: "monthly", label: t("mtd") },
      { value: "last_month", label: t("last_month") },
      { value: "yearly", label: t("ytd") },
    ],
    [i18n.language],
  );
  const values = watch(["stationId", "range", "allStation"]);
  const { refetch: apiRefetch } = useGetStationExpenseTypeService({
    stationId: values[0]?.value || null,
    range: values[1]?.value,
    allStation: values[2],
  });
  useEffect(() => {
    if (firstRender.current && props.stationsLoaded === true) {
      firstRender.current = false;
      setValue(
        "stationId",
        (props?.stations?.length > 1
          ? props?.stations?.[1]
          : props?.stations?.[0]) || null,
      );
      setTimeout(() => fetchData(), 50);
    }
  }, [props.stations]);
  useEffect(() => {
    const currentRange = watch("range");
    const updated = filters.find((f) => f.value === currentRange?.value);
    if (updated) {
      setValue("range", updated);
    }
  }, [i18n.language, filters]);
  useEffect(() => {
    if (props.globalFilterApplied === true) {
      setValue("stationId", props.globalFilters.stationId);
      setValue("range", props.globalFilters.range);
      setValue("allStation", props.globalFilters.allStation);
      setTimeout(() => fetchData(), 100);
    }
  }, [props.globalFilterApplied]);
  const fetchData = async () => {
    setLoading(true);
    await apiRefetch()
      .then((res: GetStationExpenseTypeResponse | unknown | any) => {
        if (res?.data?.success) {
          setData(res?.data?.data || {});
        } else {
          props.enqueueSnackbar(
            <AlertMessages
              text={res?.data?.message || "Something went wrong"}
            />,
            { variant: "error" },
          );
        }
      })
      .catch((err) => {
        console.error("===", err);
      })
      .finally(() => {
        setLoading(false);
        if (props.globalFilterApplied === true)
          props.setGlobalFilterApplied(false);
      });
  };
  return (
    <Card
      sx={{
        height: DASHBOARD_CHART_WIDGET_HEIGHT,
        minHeight: DASHBOARD_CHART_WIDGET_HEIGHT,
        marginTop: "16px",
        boxShadow: "none",
        borderRadius: "8px",
        display: "flex",
        flexDirection: "column",
        overflow: "hidden",
        "& .MuiCardContent-root": {
          padding: "16px !important",
          height: "100%",
          display: "flex",
          flexDirection: "column",
          boxSizing: "border-box",
          overflow: "hidden",
        },
      }}
    >
      <CardContent>
        <Box sx={dashboardWidgetHeaderSx}>
          <AnalyticsWidgetHeader
            title={t("expensesByCategory")}
            icon={<PieChartOutlineOutlined />}
            iconBg={`${props.themes?.primary || "#4F46E5"}18`}
            iconColor={props.themes?.primary || "#4F46E5"}
          />
          <Box sx={dashboardWidgetFiltersSx}>
            <Controller
              name="stationId"
              control={control}
              defaultValue={null}
              rules={{ required: t("This field is required.") }}
              render={({ field }) => (
                <Box
                  sx={{
                    "& .css-7896aj-control, & .css-1xiok1u-control": {
                      minHeight: "auto",
                      fontSize: '9px',
                      height: "auto",
                      overflow: "hidden",
                      "& .css-yzr845-indicatorContainer, & .css-1rr3n57-indicatorContainer, & .css-1asjhht-indicatorContainer, & .css-5s13ue-indicatorContainer":
                        {
                          background: "#fff",
                          padding: 0,
                          width: "15px",
                          height: "15px",
                        },
                      "& .css-pogzpp-loadingIndicator, & .css-o9ehiq-loadingIndicator":
                        { position: "absolute", right: 0 },
                      border: 0,
                    },
                  }}
                >
                  <DynamicSelect
                    label={""}
                    {...field}
                    options={props?.stations || []}
                    isDisabled={loading}
                    onChange={(selectedOption: any) => {
                      field.onChange(selectedOption);
                      if (selectedOption?.value === 0)
                        setValue("allStation", true);
                      else setValue("allStation", false);
                      setTimeout(() => fetchData(), 50);
                    }}
                    error={errors.stationId?.message as string}
                    ddWidth="30px"
                    ddHeight="30px"
                    defaultPlaceholder={t("station")}
                    isLoading={props.searchLoading === "expensesByType"}
                    onInputChange={(
                      search: string,
                      { action }: { action: string },
                    ) => {
                      if (
                        action === "input-change" ||
                        (action !== "set-value" &&
                          action !== "menu-close" &&
                          action !== "input-blur")
                      )
                        props.handleDropdownActions(
                          "expensesByType",
                          "searchIt",
                          search,
                        );
                      else if (
                        action === "input-blur" ||
                        action === "menu-close"
                      ) {
                        if (props.dropdownSearchTerm || props.pageNo > 1)
                          props.handleDropdownActions(
                            "expensesByType",
                            "searchIt",
                            "",
                          );
                        else props.setDropdownSearchTerm("");
                        props.setPageNo(1);
                      }
                    }}
                    onMenuScrollToBottom={() => {
                      if (!(props.stationsData?.pages[0]?.data as any).last)
                        props.handleDropdownActions(
                          "expensesByType",
                          "loadNext",
                        );
                    }}
                    page={props.pageNo}
                  />
                </Box>
              )}
            />
            <Controller
              name="range"
              control={control}
              defaultValue={null}
              rules={{ required: t("This field is required.") }}
              render={({ field }) => (
                <Box
                  sx={{
                    "& .css-7896aj-control, & .css-1xiok1u-control": {
                      width: screenWidth ? "60px" : "auto",
                      minHeight: "auto",
                      fontSize: '9px',
                      height: "auto",
                      overflow: "hidden",
                      "& .css-yzr845-indicatorContainer, & .css-1rr3n57-indicatorContainer, & .css-1asjhht-indicatorContainer, & .css-5s13ue-indicatorContainer":
                        {
                          background: "#fff",
                          padding: 0,
                          width: "15px",
                          height: "15px",
                        },
                      border: 0,
                    },
                  }}
                >
                  <DynamicSelect
                    label={""}
                    {...field}
                    options={filters}
                    onChange={(selectedOption: any) => {
                      field.onChange(selectedOption);
                      setTimeout(() => fetchData(), 50);
                    }}
                    error={errors.range?.message as string}
                    ddWidth="30px"
                    ddHeight="30px"
                    isDisabled={loading}
                  />
                </Box>
              )}
            />
          </Box>
        </Box>

        {loading ? (
          <>
            <Skeleton
              animation="pulse"
              sx={{ backgroundColor: props?.themes?.secondary, mt: 1 }}
            />
            <Skeleton
              animation="pulse"
              sx={{ backgroundColor: props?.themes?.secondary }}
            />
            <Skeleton
              animation="pulse"
              sx={{ backgroundColor: props?.themes?.secondary }}
            />
            <Skeleton
              animation="pulse"
              sx={{ backgroundColor: props?.themes?.secondary }}
            />
            <Skeleton
              animation="pulse"
              sx={{ backgroundColor: props?.themes?.secondary }}
            />
            <Skeleton
              animation="pulse"
              sx={{ backgroundColor: props?.themes?.secondary }}
            />
            <Skeleton
              animation="pulse"
              sx={{ backgroundColor: props?.themes?.secondary }}
            />
            <Skeleton
              animation="pulse"
              sx={{ backgroundColor: props?.themes?.secondary }}
            />
          </>
        ) : (
          <Box sx={{ width: "100%", overflowX: "auto", mt: 1 }}>
            <Expenses data={data} themes={props.themes} {...{ t }} />
          </Box>
        )}
      </CardContent>
    </Card>
  );
}
const Expenses = ({
  data,
  t,
}: {
  data: Record<string, number>;
  themes?: any;
  t: TFunction<"translation", undefined>;
}) => {
  const isLargeScreen = useMediaQuery(`@media('min-width: 1200px')`);
  const formattedData = Object.entries(data || {})
    .filter(([, value]) => value !== 0)
    .map(([key, value]) => ({
      category: key,
      shortLabel: key.length > 12 ? key.substring(0, 12) + ".." : key,
      fullLabel: key.length > 50 ? key.substring(0, 50) + ".." : key,
      expense: value,
    }))
    .sort((a, b) => b.expense - a.expense);
  const chartWidth = Math.max(300, formattedData.length * 85);
  return (
    <Box sx={{ width: chartWidth }}>
      <BarChart
        height={290}
        xAxis={[
          {
            data: formattedData.map((item) => item.shortLabel),
            scaleType: "band",
            valueFormatter: (label, context) =>
              `${context.location === "tooltip" ? formattedData.find((d) => d.shortLabel === label)?.fullLabel || "" : label}`,
          },
        ]}
        series={[
          {
            data: formattedData.map((item) => item.expense),
            label: t("expense"),
          },
        ]}
        yAxis={[
          {
            valueFormatter: (val) =>
              new Intl.NumberFormat("en", {
                notation: "compact",
                compactDisplay: "short",
                maximumFractionDigits: 2,
              }).format(Number(val)),
          },
        ]}
        skipAnimation={false}
        margin={{
          left: 40,
          right: isLargeScreen ? 0 : 10,
          top: 15,
          bottom: 35,
        }}
        grid={{ horizontal: true }}
        slotProps={{
          legend: { hidden: true },
          noDataOverlay: {
            message: t("noRecordFound"),
          },
        }}
      />
    </Box>
  );
};
